import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// prettier-ignore
import { Button, Hero, GetYourGuideTeaser as UpgradesTeaser, Modal } from '@cfm-media/pwa-ui-components';
import { LoadableStatus } from '../mobx-utils';
import { parseHtml } from '../core/utils';
import { useStore } from '../core/store';
import { useTranslation } from '../core/utils/translations';

import { ConfirmBookingModal } from '../components/confirm-booking-modal';
import { ContentWrapper } from '../components/content-wrapper';
import { ErrorPage } from '../components/error-page';
import { LoadSpinner } from '../components/loader';
import { Modal as BookingModal } from '../components/modal';
import { PageWrapper } from '../components/page-wrapper';
import { upgradeBooking } from '../stores/flows/upgrade';
import { urls } from '../urls';

export const Upgrades = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation('upgrades-page');

  const [bookingNotPossible, setBookingNotPossibleModal] = useState(false);

  const [openBookingModal, setOpenBookingModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const [showSpinner, setShowSpinner] = useState(false);

  const [bookingConfig, setBookingConfig] = useState({});

  const { upgrades, booking } = {
    ...useStore().UpgradeStore,
    ...useStore().BookingStore,
  };

  const { create, destroy, modal } = useUpgradesModal();

  useEffect(() => {
    if (!upgrades.data) upgrades.fetch();
    if (!booking.data) booking.fetch();
  }, []);

  // prettier-ignore
  if ([upgrades?.status, booking?.status].some((status) => status === LoadableStatus.error)) {
      return <UpgradesError />;
  } else if ([upgrades?.status, booking?.status].some((status) => status !== LoadableStatus.done)) {
      return <LoadSpinner />;
  }

  let isSwiss = booking.data.payment.currency != 'EUR';

  let upgradesData = upgrades.data;
  let alreadyBookedUpgrades;

  const alreadyBookedUpgradesCodes = booking.data.bookedUpgrades;

  const isInkasso = booking?.data?.payment?.isInkasso;
  const agencyNumber = booking?.data?.agency?.id;
  const billNumber = booking?.data?.id;

  if (alreadyBookedUpgradesCodes) {
    upgradesData = upgrades.data.filter(
      (upgrade) => !alreadyBookedUpgradesCodes.includes(upgrade?.code),
    );
    alreadyBookedUpgrades = upgrades.data.filter((upgrade) =>
      alreadyBookedUpgradesCodes.includes(upgrade?.code),
    );
  }

  return (
    <PageWrapper>
      <Hero
        image="public/assets/images/teasers/upgrades.jpg"
        iconClass="icon-services"
        headline="Zusatzleistungen"
        overlay
      />
      {showSpinner ? <LoadSpinner /> : null}

      <ContentWrapper>
        {!upgradesData?.length ? (
          <p className="mt-6 text-center text-gray-500">
            {t('upgrades-empty-text')}
          </p>
        ) : null}

        {alreadyBookedUpgrades?.length ? (
          <>
            {' '}
            <div className="text-xl text-brand-1 font-semibold uppercase mb-6">
              Ihre gebuchten Leistungen:
            </div>
            <div className="bg-white shadow-lg mb-5">
              <div className="px-3 py-3">
                {alreadyBookedUpgrades?.map((upgrade) => {
                  return (
                    <div className="border-b-2 border-text-gray-900">
                      <div className="flex items-center justify-between  my-2">
                        <div className="flex items-center">
                          <img
                            className="w-12 h-12 mr-3 !rounded-full"
                            src={upgrade.imageUrl}
                            alt="Bentour Reisen"
                          />
                          <strong className="block text-sm sm:text-base text-gray-900 mr-3">
                            {upgrade?.name}
                          </strong>
                        </div>
                        <span className="icon icon-check-circle text-brand-2 text-lg" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <br />
          </>
        ) : null}

        {alreadyBookedUpgrades?.length ? (
          <div className="mb-3 text-xl text-brand-1 font-semibold uppercase">
            {t('available-upgrades-title')}
          </div>
        ) : null}

        {upgradesData
          ?.slice()
          ?.sort((a, b) => a.sortingField - b.sortingField)
          ?.map((upgrade) => {
            const createModal = create.bind(
              null,
              upgrade.name,
              upgrade.description,
            );

            let price;
            let currency;

            if (isSwiss) {
              price = upgrade.priceChf;
              currency = 'CHF';
            } else {
              price = upgrade.priceEuro;
              currency = '€';
            }

            return (
              <UpgradesTeaser
                key={upgrade.name}
                image={upgrade.imageUrl}
                headline={upgrade.name}
                onMoreInfoClick={createModal}
                price={price}
                currency={currency}
                button={
                  <UpgradeButton
                    onClick={() => {
                      if (!isInkasso) {
                        setBookingNotPossibleModal(true);
                      } else {
                        setBookingConfig({
                          name: upgrade.name,
                          price: price,
                          originalPrice: booking?.data?.payment?.gross,
                          currency: currency,
                          agencyNumber: agencyNumber,
                          billNumber: billNumber,
                          SpecialServiceCode: upgrade?.code,
                        });
                        setOpenBookingModal(true);
                      }
                    }}
                  />
                }
                translations={{
                  priceProPersonText:
                    upgrade.priceType === 'Group' ? 'pro Gruppe' : 'pro Person',
                }}
              />
            );
          })}
      </ContentWrapper>

      <ConfirmBookingModal
        bookingConfig={bookingConfig}
        show={openBookingModal}
        buttonText={'Buchung bestätigen'}
        setShow={setOpenBookingModal}
        onClose={() => {
          upgrade(
            bookingConfig,
            setOpenSuccessModal,
            setShowSpinner,
            setOpenErrorModal,
            booking,
          );
          setOpenBookingModal(false);
          setShowSpinner(true);
        }}
      />

      <BookingModal
        show={openSuccessModal}
        headline={'Buchung erfolgreich!'}
        text={
          'Die Buchung war erfolgreich. Sie erhalten in Kürze eine neue Bestätigung bzw. Rechnung.'
        }
        buttonText={'Okay'}
        onClose={() => {
          setOpenSuccessModal(false);
          navigate(urls.home);
        }}
      />

      <BookingModal
        type="error"
        show={bookingNotPossible}
        headline={'Buchung nicht möglich!'}
        text={
          'Da die Bezahlung im Reisebüro erfolgt, ist eine Buchung über myBentour nicht möglich. Bitte wenden Sie sich an Ihr Reisebüro'
        }
        buttonText={'Okay'}
        onClose={() => {
          setBookingNotPossibleModal(false);
        }}
      />

      <BookingModal
        type="error"
        show={openErrorModal}
        headline={'Buchung fehlgeschlagen!'}
        text={
          'Es tut uns leid, leider ist bei der Buchung ein Fehler aufgetreten. Bitte wenden Sie sich an Ihr Reisebüro.'
        }
        buttonText={'Okay'}
        onClose={() => {
          setOpenErrorModal(false);
          navigate(urls.home);
        }}
      />

      <Modal
        className="pwa-modal-content"
        isOpen={modal.isVisible}
        key={modal.key}
        headline={modal.headline}
        setIsOpen={destroy}
        children={parseHtml(modal.content)}
        closeButton
      />
    </PageWrapper>
  );
});

export const UpgradesError = () => (
  <PageWrapper>
    <Hero
      image="public/assets/images/teasers/upgrades.jpg"
      iconClass="icon-services"
      headline="Zusatzleistungen"
      overlay
    />

    <ContentWrapper>
      <ErrorPage
        title="Keine Zusatzleistungen gefunden"
        body="Für diese Buchung sind leider keine Zusatzleistungen verfügbar."
        link={urls.home}
        buttonText="Zurück zur Startseite"
      />
    </ContentWrapper>
  </PageWrapper>
);

const UpgradeButton = ({ onClick }) => (
  <Button onButtonClick={onClick} title="Buchen" block variant="primary" />
);

function useUpgradesModal() {
  const defaultModal = {
    content: null,
    headline: null,
    isVisible: false,
    key: Date.now(),
  };

  const [modal, setModal] = useState(defaultModal);

  const create = (headline, content) =>
    setModal({ isVisible: true, headline, content, key: Date.now() });

  const destroy = setModal.bind(null, defaultModal);

  return { create, destroy, modal };
}

async function upgrade(
  bookingConfig,
  triggerSuccessModal,
  setShowSpinner,
  setOpenErrorModal,
  booking,
) {
  try {
    const bookingResponse = await upgradeBooking(bookingConfig);

    if (bookingResponse?.success) {
      try {
        await booking.fetch();
      } catch (error) {
        console.error(error);
      }

      setShowSpinner(false);
      triggerSuccessModal(true);
    } else {
      setShowSpinner(false);
      setOpenErrorModal(true);
    }
  } catch (error) {
    console.log(error);
  }
}
